import React, { useContext } from "react"
import CloseModal from "../CloseModal"
import { Button } from "../ui/button"
import { CoachPrivateProfileContext } from "../../pages/manage-business/profile/context/coachPrivateProfileContext"
import { SvgArrowLeftRight } from "../icons/arrow-left-right"
import { SvgImage, SvgCrop } from "../icons"
import { CommonFunctionCtx } from "../../context/commonFunctionContext"
import DragDropArea from "../DragDropArea"
import ReactCrop, {
  centerCrop,
  type Crop,
  makeAspectCrop,
} from "react-image-crop"
import "react-image-crop/dist/ReactCrop.css"

interface Props {
  handleClick: any
  bentoSelection: number
  updateBento: (
    bentoSpot: number | null,
    selection: string,
    contentId?: number,
    content?: string,
    url?: string
  ) => void
}

export default function BentoImageFlow({
  handleClick,
  updateBento,
  bentoSelection,
}: Props) {
  const { setPopupNotification } = React.useContext(CommonFunctionCtx)
  const [imageFile, setImageFile] = React.useState<File>()
  const [imageProperties, setImageProperties] = React.useState<any>({
    base64: "",
    type: "",
  })
  const [previewImgUrl, setPreviewImgUrl] = React.useState<string>("")
  const [showCrop, setShowCrop] = React.useState<boolean>(false)
  const [crop, setCrop] = React.useState<Crop>()
  const [imgRef, setImgRef] = React.useState<HTMLImageElement | null>(null)
  const { coachPublicProfileData } = useContext(CoachPrivateProfileContext)

  const validateFile = (imageFile: File) => {
    if (
      (imageFile.type === "image/png" ||
        imageFile.type === "image/jpg" ||
        imageFile.type === "image/jpeg") &&
      imageFile.size <= 5000000 &&
      imageFile.size > 0
    ) {
      return true
    } else {
      return false
    }
  }

  const getImageProperties = () => {
    if (imageFile) {
      if (validateFile(imageFile)) {
        const type = imageFile.type.split("/")[1]
        const reader = new FileReader()
        reader.readAsDataURL(imageFile)
        reader.onload = function () {
          const string = (reader.result as string).split("\n").map((data) => {
            return data.split(",")
          })
          const base64 = string[0][1]
          setImageProperties({ base64: base64, type: type })
        }
        reader.onerror = function (error) {
          console.log("Error: ", error)
        }
      } else {
        setPopupNotification({
          show: true,
          title: "Bummer! Your file is too big.",
          message:
            "Give it another try with a file under 5 megabytes, and in one of the following formats: png, jpg, or jpeg.",
          callback: null,
        })
        setImageFile(undefined)
        setImageProperties({ base64: "", type: "" })
      }
    }
  }

  const getCroppedImg = (
    image: HTMLImageElement,
    crop: Crop,
    fileName: string
  ): Promise<{ base64: string; type: string }> => {
    const canvas = document.createElement("canvas")
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    // Convert percentage crop to pixel values
    const cropX = (crop.x! / 100) * image.width * scaleX
    const cropY = (crop.y! / 100) * image.height * scaleY
    const cropWidth = (crop.width! / 100) * image.width * scaleX
    const cropHeight = (crop.height! / 100) * image.height * scaleY

    canvas.width = cropWidth
    canvas.height = cropHeight

    const ctx = canvas.getContext("2d")
    if (!ctx) {
      throw new Error("No 2d context")
    }

    ctx.imageSmoothingQuality = "high"

    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    )

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"))
            return
          }
          const reader = new FileReader()
          reader.readAsDataURL(blob)
          reader.onload = function () {
            const string = (reader.result as string).split("\n").map((data) => {
              return data.split(",")
            })
            const base64 = string[0][1]
            const type = blob.type.split("/")[1]
            resolve({ base64, type })
          }
          reader.onerror = reject
        },
        "image/jpeg",
        0.95
      )
    })
  }

  const handleCropComplete = async () => {
    if (!imgRef || !crop || !imageFile) return

    try {
      const { base64, type } = await getCroppedImg(imgRef, crop, imageFile.name)
      setImageProperties({
        base64: base64,
        type: type,
      })
      // Update preview with cropped image
      const croppedDataUrl = `data:image/${type};base64,${base64}`
      setPreviewImgUrl(croppedDataUrl)
      setShowCrop(false)
    } catch (error) {
      console.error("Error cropping image:", error)
      setPopupNotification({
        show: true,
        title: "Error cropping image",
        message: "Failed to crop image. Please try again.",
        callback: null,
      })
    }
  }

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { naturalWidth: width, naturalHeight: height } = e.currentTarget

    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        1,
        width,
        height
      ),
      width,
      height
    )

    setCrop(crop)
  }

  React.useEffect(() => {
    getImageProperties()
    if (imageFile) {
      setPreviewImgUrl(URL.createObjectURL(imageFile))
    }
  }, [imageFile])

  return (
    <div className="fixed top-0 left-0 w-screen h-screen flex items-center justify-center zoee-backdrop-filter z-[1009]">
      <div className="w-[775px] max-w-[90%] main-shadow rounded-[16px] pt-[76px] pb-[40px] mt-[90px] px-[16px] md:px-[55px] max-h-[85vh] bg-white fixed flex flex-col items-center overflow-y-auto">
        <CloseModal
          callback={() => {
            handleClick("cancel")
          }}
          styling="absolute top-[24px] right-[32px] flex items-center justify-center w-[32px] h-[32px] rounded-full bg-grayFlash"
        />
        <h2 className="text-center top-[10px] left-[10px] sm:left-[30px] sm:top-[30px] md:left-[50px] text-[16px] md:text-[28px] lg:text-[16px] sm:mt-0">
          Featured Content
        </h2>
        <div className="flex gap-x-2 justify-center items-center">
          <SvgImage height="40" width="40" />
          <p className="font-bold text-[20px] sm:mt-4 md:-translate-y-2">
            Image
          </p>
        </div>
        <Button
          variant={"link"}
          onClick={() => {
            handleClick("switch content")
          }}
        >
          <SvgArrowLeftRight />
          Switch content type
        </Button>

        <div className="flex flex-col justify-center items-center mt-4 w-full">
          <div className="flex flex-col gap-[8px] w-full">
            <div className="flex items-center justify-between gap-[8px]">
              <p className="font-bold text-[16px]">Upload Image</p>
              <span className="font-normal text-sm text-graySlate">
                Required
              </span>
            </div>
            <DragDropArea
              styling={`w-full rounded-[16px] flex-row h-[100px]`}
              mediaFile={imageFile}
              setMediaFile={setImageFile}
              mediaType="image"
            />
          </div>
          <div className="flex flex-col justify-center items-center rounded-[16px] mt-4">
            {showCrop ? (
              <div className="flex w-full flex-col gap-6">
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  aspect={1}
                  className="min-h-[60px] min-w-[60px]"
                >
                  {previewImgUrl && (
                    <img
                      ref={setImgRef}
                      src={previewImgUrl}
                      alt="preview"
                      onLoad={onImageLoad}
                      className="max-h-[400px] w-full object-contain"
                    />
                  )}
                </ReactCrop>
                <div className="mx-auto flex gap-2">
                  <Button onClick={() => setShowCrop(false)} variant="outline">
                    Cancel
                  </Button>
                  <Button onClick={handleCropComplete}>Apply Crop</Button>
                </div>
              </div>
            ) : (
              <>
                {previewImgUrl ? (
                  <div className="flex flex-col gap-4 items-center">
                    <div className="w-[200px] rounded-[16px] h-[200px]">
                      <img
                        src={previewImgUrl}
                        alt="preview"
                        className="w-full h-full rounded-[16px] object-cover"
                      />
                    </div>
                    <Button
                      variant="utility"
                      onClick={() => {
                        setShowCrop(true)
                      }}
                    >
                      <SvgCrop />
                      Crop Image
                    </Button>
                  </div>
                ) : (
                  <p className="text-graySlate text-center text-sm min-w-[300px] mb-5">
                    Max file size is 5MB. Recommended 300 x 300 px at 150 DPI.{" "}
                    <br />
                    File types: .png, .jpg, and .jpeg
                  </p>
                )}
                <p className="text-blurple text-center">
                  Don't have an image?
                </p>
                <p className="text-blurple text-center">
                  Get a free stock photo here{" "}
                  <a
                    href="https://pixabay.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://pixabay.com
                  </a>
                </p>
              </>
            )}
          </div>
        </div>
        {!showCrop && (
          <div className="flex gap-x-2 mt-auto">
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                handleClick("cancel")
              }}
            >
              Cancel
            </Button>
            <Button
              size="lg"
              variant="default"
              onClick={() => {
                updateBento(
                  bentoSelection,
                  "image",
                  undefined,
                  undefined,
                  imageProperties?.base64
                    ? `data:image/${imageProperties.type};base64,${imageProperties.base64}`
                    : undefined
                )
                handleClick("cancel")
              }}
              disabled={!imageFile}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
